// #region imports
import { UseSheetTheme } from '../hooks/useSheetTheme';
import { ReturnTypeofSheetSectionsSelector } from '../selectors/sheetUiSelector';
import {
  getUnHiddenPosition,
  getViewportStartEnd,
} from '../utils/views/virtualViewport';
import { doesRangeInterects, isRangeInsideRanges } from '../utils/area/range';
import { HeaderGroup } from '../types/sheet/headerGroups';
import {
  getFreezeBarHandleSize,
  getSizeForViewportIndex,
} from '../utils/viewport/viewportUtils';
import { inRange } from '../../core/utils/general/general';
import { SheetDefaults } from '../constants/sheetDefaults';
import { WindowGeometry } from '../../windows/types';
import { DataDirection } from '../../core/types/ui/position';
import { Freeze } from '../types';
import { getEntitiesData } from '../utils/sheet/uiData';
// #endregion

// #region types
interface RenderGroupsProps
  extends ReturnTypeofSheetSectionsSelector,
    Pick<UseSheetTheme, 'isRtl'> {
  sheetId: string;
}

export interface GetGroupParams extends WindowGeometry {
  group: HeaderGroup;
  // groupId: string;
  isLastElementInViewport?: boolean;
  isClosed?: boolean;
}

type GetGroupView = (params: GetGroupParams) => JSX.Element;
// #endregion

export const startRenderGroups = (
  props: RenderGroupsProps,
  getGroupView: GetGroupView,
  getGroupControlView: GetGroupView,
  isColumn = false
) => {
  // #region variables
  const viewport = isColumn ? props.colViewport : props.rowViewport;
  const frozenData = isColumn ? props.freezedCols : props.freezedRows;
  const groupsData = isColumn ? props.colGroups : props.rowGroups;

  const Groups: JSX.Element[] = [];
  const collectiveViewport = viewport.frozenIndexes.concat(
    viewport.nonFrozenIndexes
  );
  const collectiveViewportSizes = viewport.frozenSizes.concat(
    viewport.nonFrozenSizes
  );

  const entityData = getEntitiesData(
    { ...props, colSizes: [], rowSizes: [] }, // We don't need colSizes & rowSizes here
    { filterResults: props.filterResults },
    isColumn
  );

  const firstPosition = getUnHiddenPosition(
    [], // entityData.entitySizes, We don't need entity sizes??
    entityData.hiddenRanges,
    0,
    entityData.maxIndex,
    DataDirection.Forward,
    entityData.freeze,
    entityData.filterResults,
    0
  );
  // #endregion

  // #region iterate over groups and render groups that are part of viewport
  for (let group of groupsData) {
    const { nestedGroups, ...groupRange } = group;

    const frozenRange = getViewportStartEnd(viewport.frozenIndexes);
    const nonFrozenRange = getViewportStartEnd(viewport.nonFrozenIndexes);

    // Will include previous index for group control
    const temporaryGroupRange = {
      ...groupRange,
      start: Math.max(0, groupRange.start - 1),
    };

    if (
      (frozenData.frozen &&
        doesRangeInterects(temporaryGroupRange, frozenRange)) ||
      doesRangeInterects(temporaryGroupRange, nonFrozenRange)
    ) {
      renderGroup(
        props,
        collectiveViewport,
        collectiveViewportSizes,
        group,
        getGroupView,
        getGroupControlView,
        Groups,
        firstPosition,
        isColumn
      );
    }
  }
  // #endregion

  return Groups;
};

// #region render group
const renderGroup = (
  props: RenderGroupsProps,
  collectiveViewport: number[],
  collectiveViewportSizes: number[],
  group: HeaderGroup,
  getGroupView: GetGroupView,
  getGroupControlView: GetGroupView,
  Groups: JSX.Element[],
  firstPosition: number,
  isColumn: boolean,
  intialTop = 0,
  initialLeft = 0
) => {
  // #region variables and checks
  const hiddenRanges = isColumn ? props.hiddenCols : props.hiddenRows;
  const freeze = isColumn ? props.freezedCols : props.freezedRows;
  const freezeBarSize = getFreezeBarHandleSize(freeze, props.zoom);

  const groupGeometry: WindowGeometry = {
    x: initialLeft,
    width: 0,
    height: 0,
    y: intialTop,
  };

  const groupControlGeometry: WindowGeometry = {
    ...groupGeometry,
  };
  let isPreviousElementInViewport = false;
  let isLastElementInViewport = false;

  // const entitySizes = isColumn ? props.colSizes : props.rowSizes;
  const defaultSize = isColumn ? props.defaultColWidth : props.defaultRowHeight;

  const viewportRange = getViewportStartEnd(collectiveViewport);
  if (group.start - 1 > viewportRange.end) return;
  // if (group.end < viewportRange.start) return;
  // #endregion

  // #region calculate size and position
  // for (let viewportIndex of collectiveViewport) {
  for (let [index, viewportIndex] of collectiveViewport.entries()) {
    if (viewportIndex > group.end) break;

    const size = getSizeForViewportIndex(
      collectiveViewportSizes,
      index,
      defaultSize,
      props.zoom
    );

    // const item = entities[viewportIndex];
    // const size = getRowColSize(defaultSize, item, props.zoom);

    if (viewportIndex === group.end) {
      isLastElementInViewport = true;
    }

    isPreviousElementInViewport = createGroupController(
      props,
      group,
      viewportIndex,
      size,
      firstPosition,
      groupGeometry.x, //x,
      groupGeometry.y, //y,
      groupControlGeometry,
      isColumn,
      isPreviousElementInViewport
    );

    if (viewportIndex < group.start) {
      if (isColumn) groupGeometry.x += size;
      else groupGeometry.y += size;
    }
    if (inRange(viewportIndex, group.start, group.end)) {
      if (isColumn) groupGeometry.width += size;
      else groupGeometry.height += size;
    }
  }
  // #endregion

  // #region render group control if previous element in viewpor
  const defaultWidth = SheetDefaults.rowGroupBarWidth * props.zoom;
  const defaultHeight = SheetDefaults.groupBarHeight * props.zoom;

  if (isPreviousElementInViewport)
    Groups.push(
      getGroupControlView({
        ...groupControlGeometry,
        group,
        width: isColumn ? groupControlGeometry.width : defaultWidth,
        height: isColumn ? defaultHeight : groupControlGeometry.height,
        isClosed: isRangeInsideRanges(
          { start: group.start, end: group.end },
          hiddenRanges
        ),
      })
    );
  // #endregion

  // #region render Group also add freeze bar size if needed
  const { ultimateWidth, ultimateHeight } = createGroupDimensions(
    group,
    freeze,
    groupGeometry,
    defaultWidth,
    defaultHeight,
    freezeBarSize,
    isColumn
  );
  if (groupGeometry.width !== 0 || groupGeometry.height !== 0)
    Groups.push(
      getGroupView({
        group,
        x: groupGeometry.x,
        width: ultimateWidth,
        y: groupGeometry.y,
        height: ultimateHeight,
        isLastElementInViewport,
      })
    );
  // #endregion

  // Render nested groups
  group.nestedGroups.forEach((nestedGroup) => {
    renderGroup(
      props,
      collectiveViewport,
      collectiveViewportSizes,
      nestedGroup,
      getGroupView,
      getGroupControlView,
      Groups,
      firstPosition,
      isColumn,
      isColumn ? groupGeometry.y + defaultHeight : 0,
      isColumn ? 0 : groupGeometry.x + defaultWidth
    );
  });
};
// #endregion

// #region helpers
const createGroupController = (
  props: RenderGroupsProps,
  group: HeaderGroup,
  viewportIndex: number,
  size: number,
  firstPosition: number,
  x: number,
  y: number,
  groupControlGeometry: WindowGeometry,
  isColumn: boolean,
  isPrevElementInViewport: boolean
) => {
  const freeze = isColumn ? props.freezedCols : props.freezedRows;
  const freezeBarSize = getFreezeBarHandleSize(freeze, props.zoom);

  let isPreviousElementInViewport = isPrevElementInViewport;
  const prevIndex = group.start - 1;
  if (viewportIndex === prevIndex) {
    isPreviousElementInViewport = true;
    if (isColumn) {
      groupControlGeometry.x +=
        freeze.frozen && prevIndex >= freeze.end ? x + freezeBarSize : x;
      groupControlGeometry.width = size;
    } else {
      groupControlGeometry.y =
        freeze.frozen && prevIndex >= freeze.end ? y + freezeBarSize : y;
      groupControlGeometry.height = size;
    }
  } else if (viewportIndex === firstPosition && group.start === 0) {
    isPreviousElementInViewport = true;
    if (isColumn) {
      groupControlGeometry.x = -props.rowHeaderWidth;
      groupControlGeometry.width = props.rowHeaderWidth;
    } else {
      groupControlGeometry.y = -(props.colFilterHeight + props.colHeaderHeight);
      groupControlGeometry.height =
        props.colFilterHeight + props.colHeaderHeight;
    }
  }

  return isPreviousElementInViewport;
};

const createGroupDimensions = (
  group: HeaderGroup,
  freeze: Freeze,
  groupGeometry: WindowGeometry,
  defaultWidth: number,
  defaultHeight: number,
  freezeBarSize: number,
  isColumn: boolean
) => {
  let visibleWidth = groupGeometry.width;
  if (isColumn && freeze.frozen) {
    if (group.start < freeze.end && group.end > freeze.end)
      visibleWidth += freezeBarSize;
    else if (group.start > freeze.end) groupGeometry.x += freezeBarSize;
  }

  const ultimateWidth = isColumn ? visibleWidth : defaultWidth;

  let visibleHeight = groupGeometry.height;
  if (!isColumn && freeze.frozen) {
    if (group.start < freeze.end && group.end > freeze.end)
      visibleHeight += freezeBarSize;
    else if (group.start > freeze.end) groupGeometry.y += freezeBarSize;
  }
  const ultimateHeight = isColumn ? defaultHeight : visibleHeight;

  return {
    ultimateHeight,
    ultimateWidth,
  };
};

// #endregion
